import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Google Material Symbols — the shared icon engine for the Shrine icon blocks/sections (the Shopify
 * model: the merchant types a glyph NAME, e.g. `check_circle`, and the webfont renders it via ligatures,
 * so any of the ~3000 icons is reachable without a curated SVG set). ONE source of truth for the font
 * URL, the legacy-id → symbol back-compat map, and the `<MaterialIcon>` renderer — consumed by both the
 * `icons-with-content` section and the `icon-with-text` / `icon-with-content` product blocks, so the
 * legacy map and font can never drift between them.
 */
import { useEffect } from 'react';
import { useRenderContextOptional } from '../../renderer/RenderProvider.js';
/**
 * The variable Material Symbols Outlined font — PERF-TUNED. The full 4-axis range
 * (`wght@100..700,GRAD@-50..200`) ships a ~4 MB webfont that, render-blocking on slow networks, saturated
 * the connection and starved the LCP image (FCP/LCP ~22s). {@link MaterialIcon} only ever uses `wght 400`
 * and `GRAD 0`, and the optical size is barely perceptible, so we PIN `wght@400,GRAD@0,opsz@24` and keep
 * just `FILL@0..1` (filled icons). Even pinned, the FULL glyph set is ~456 KB — still the page's heaviest
 * transfer. So the storefront goes further: it ships only the icons the page ACTUALLY uses via the
 * `icon_names=` SUBSET ({@link materialSymbolsHrefFor}, fed by PageRenderer) → a few KB. preconnect to the
 * font origins lives ONCE in the storefront <head> (Layout.astro), not per block (avoids the
 * >4-preconnect warning).
 *
 * `display=block`, NOT `swap`: these glyphs are drawn from LIGATURE TEXT (`check_circle`), so a swap
 * period paints the literal WORD in a fallback font — much wider than the glyph, so it shifts layout too.
 * `block` keeps the box empty until the font lands, which is the right trade for a decorative icon.
 *
 * `display=*` only governs how the FONT FACE paints while loading; it never stopped the `<link>` ITSELF
 * from blocking first paint — that is a separate mechanism, and it is what Lighthouse reports as
 * "render blocking requests" (670 ms on the reference PDP). {@link MaterialSymbolsFont} fixes that.
 */
const MATERIAL_SYMBOLS_QUERY = 'family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0..1,0';
/**
 * FULL (un-subset) Material Symbols href — every glyph, ~456 KB. The FALLBACK when the page's exact icon
 * set isn't known: the builder (`edit` render), a Storybook story, or a block rendered with no render
 * context. The storefront replaces it with a tiny {@link materialSymbolsHrefFor} subset via the context.
 */
export const MATERIAL_SYMBOLS_HREF = `https://fonts.googleapis.com/css2?${MATERIAL_SYMBOLS_QUERY}&display=block`;
/**
 * Build the Material Symbols href for an EXACT set of glyph names via Google Fonts' `icon_names=` subset
 * param — the page ships only the icons it uses (~a few KB) instead of the full ~456 KB webfont. Empty
 * set → the full font (nothing to subset to). Names are de-duplicated + sorted so the URL — and thus the
 * browser/CDN cache key — stays STABLE for a given icon set across renders.
 */
export function materialSymbolsHrefFor(names) {
    const unique = [...new Set(names)].filter(Boolean).sort();
    if (unique.length === 0)
        return MATERIAL_SYMBOLS_HREF;
    return `https://fonts.googleapis.com/css2?${MATERIAL_SYMBOLS_QUERY}&icon_names=${unique.join(',')}&display=block`;
}
/**
 * Back-compat: documents authored before the Material Symbols switch stored either the builder's curated
 * icon ids (hyphenated: `shield-check`, `check-circle`, `return`…) or a few emoji-map names that are NOT
 * valid Material Symbols (`truck`, `leaf`, `tag`, `phone`…). Map those to a real symbol name; anything
 * already a valid symbol name (or a freshly-typed one) passes through unchanged.
 */
export const LEGACY_ICON_TO_SYMBOL = {
    // builder ICON_SET curated ids
    delivery: 'local_shipping',
    package: 'inventory_2',
    return: 'assignment_return',
    'shield-check': 'verified_user',
    'check-circle': 'check_circle',
    clock: 'schedule',
    'credit-card': 'credit_card',
    discount: 'sell',
    cash: 'payments',
    gift: 'card_giftcard',
    heart: 'favorite',
    globe: 'public',
    // legacy emoji-map names that are not valid Material Symbols
    truck: 'local_shipping',
    leaf: 'eco',
    tag: 'sell',
    phone: 'call',
    shield: 'verified_user',
};
/** A plausible Material Symbols token (snake_case ascii). A non-matching value (e.g. a pasted emoji) is
 *  rendered as literal text so the "paste an emoji" escape hatch still works. */
export const SYMBOL_TOKEN = /^[a-z0-9_]+$/;
/**
 * Resolve a stored icon value to a Material Symbols code name; empty → `check_circle`. Accepts the human
 * TITLE shown on fonts.google.com/icons (e.g. "Percent Discount") and normalises it to the code name the
 * webfont expects (lowercase, spaces → underscores → `percent_discount`), so a merchant can paste either.
 */
export function resolveSymbolName(raw) {
    const key = (raw ?? '').trim().toLowerCase();
    if (!key)
        return 'check_circle';
    return LEGACY_ICON_TO_SYMBOL[key] ?? key.replace(/\s+/g, '_');
}
/**
 * True when {@link MaterialIcon} would actually paint something for `name`: a valid Material Symbols token
 * or an emoji. Empty input, or a mistyped/invalid NAME (spaces/words, e.g. "Percent Discount"), returns
 * false — so a host can skip rendering the icon (and its webfont) instead of showing broken literal text.
 */
export function iconRenders(name) {
    if (!name || !name.trim())
        return false;
    const s = resolveSymbolName(name);
    return SYMBOL_TOKEN.test(s) || !/[a-z]/i.test(s);
}
/**
 * The glyph class, plus the BOX CLAMP that keeps a not-yet-loaded icon from re-laying out the page.
 *
 * `display=block` on the font URL was believed to make the loading icon harmless ("keeps the box empty
 * until the font lands"). It does not: a blocked face paints NOTHING but still lays the ligature text out
 * with the FALLBACK font's metrics, so the span keeps the width of the WORD. Measured on the Arabic PDP
 * of a live store (Pixel 5, 393 px): `published_with_changes` was 395.5 px wide, the document went
 * 393 → 399 px, and Chrome widened the mobile layout viewport to fit. In RTL the page is anchored to the
 * RIGHT edge, so those 6 px moved the WHOLE page sideways for ~0.75 s until the font landed and it
 * snapped back. Worse, before {@link PROMOTE_JS} flips the sheet to `media="all"` there is no `@font-face`
 * at all, so the literal word is not just measured — it is PAINTED.
 *
 * So until the glyphs are actually usable, every icon is a hidden 1em square: the word can neither widen
 * the layout nor be seen. The clamp is scoped to `html:not([data-aqx-symbols-ready])` on purpose — once
 * the attribute lands this rule stops matching entirely and the painted icon is byte-for-byte the box it
 * has always been (an `overflow:hidden` inline-block takes its baseline from the bottom margin edge, which
 * would nudge icons sitting inline with text; scoping keeps that out of the loaded state).
 *
 * The reveal has TWO states, because the first shipped version still twitched. Measured on the live PDP
 * right after deploy: the safety timer fired at 8 309 ms while the font was still in flight and landed at
 * 8 441 ms, so un-clamping handed the fallback word its full width back for 132 ms — the same sideways
 * slide, just 30× shorter. `fallback` therefore only restores VISIBILITY and keeps the 1em box: a glyph
 * we could not load may show a clipped letter, but it can never move the page again. Only `font` — the
 * glyphs are really there — drops the clamp.
 */
const FONT_CSS = `.material-symbols-outlined{font-family:'Material Symbols Outlined';font-weight:normal;font-style:normal;` +
    `line-height:1;letter-spacing:normal;text-transform:none;display:inline-block;white-space:nowrap;` +
    `word-wrap:normal;direction:ltr;-webkit-font-feature-settings:'liga';font-feature-settings:'liga';` +
    `-webkit-font-smoothing:antialiased}` +
    `html:not([data-aqx-symbols-ready]) .material-symbols-outlined{inline-size:1em;block-size:1em;` +
    `overflow:hidden;visibility:hidden}` +
    `html[data-aqx-symbols-ready=fallback] .material-symbols-outlined{inline-size:1em;block-size:1em;` +
    `overflow:hidden}`;
/** Un-clamps the glyphs when scripting is off: nothing will ever set the ready attribute, so the
 *  `<noscript>` sheet (same specificity, later in the document) must win and show the icons. */
const NOSCRIPT_CSS = `html:not([data-aqx-symbols-ready]) .material-symbols-outlined{inline-size:auto;block-size:auto;` +
    `overflow:visible;visibility:visible}`;
/** The `@font-face` family + a size, as the Font Loading API wants it. */
export const SYMBOLS_FONT_SPEC = "1em 'Material Symbols Outlined'";
/** How long the clamp may hold the icons hidden before falling back. Mirrors the ~3 s block period of
 *  `display=block`: past it the browser would paint the fallback word anyway, so revealing then is never
 *  worse than shipping no clamp at all. The timer DEFERS while `document.fonts` is still loading, up to
 *  {@link SYMBOLS_REVEAL_DEADLINE_MS} — firing on a font that was merely slow is what cost 132 ms of
 *  sideways slide on the live page. */
export const SYMBOLS_REVEAL_TIMEOUT_MS = 3000;
/** Hard cap from DOMContentLoaded: past it the icons are revealed whatever `document.fonts` claims, so a
 *  hung font request (ours or the theme's) can never hide them for good. */
export const SYMBOLS_REVEAL_DEADLINE_MS = 10000;
/**
 * Promotes the icon stylesheet from `media="print"` (fetched, but never render-blocking) to `media="all"`
 * once it has actually loaded, then drops the {@link FONT_CSS} clamp by stamping `data-aqx-symbols-ready`
 * on `<html>`. Guarded by a window flag so the N copies a page emits cost one run. Inline because it must
 * beat first paint; allowed by the storefront CSP (`script-src 'self' 'unsafe-inline'`).
 *
 * The reveal waits for the FONT FILE, not for the sheet. Promoting the sheet only registers the
 * `@font-face`; until the file itself lands the glyphs are still measured with the fallback's metrics, so
 * revealing at promote time would hand back the full width of the ligature word — on the measured PDP
 * that was a second 400 ms window of the exact sideways shift this clamp exists to kill. Three escape
 * hatches keep an icon from hiding forever: the sheet's `error`, a rejected `fonts.load`, and the
 * {@link SYMBOLS_REVEAL_TIMEOUT_MS} timer.
 */
const PROMOTE_JS = `(function(){if(window.__aqxSymbolsFont)return;window.__aqxSymbolsFont=1;` +
    `var R=document.documentElement,S=0,SPEC=${JSON.stringify(SYMBOLS_FONT_SPEC)},D=0;` +
    // S: 0 clamped · 1 fallback (visible, still boxed) · 2 font (clamp dropped). Never walks back.
    `function mark(v){if(v<=S)return;S=v;R.setAttribute('data-aqx-symbols-ready',v===2?'font':'fallback')}` +
    `function fb(){mark(1)}` +
    `function glyphs(){try{if(document.fonts&&document.fonts.load){document.fonts.load(SPEC).then(` +
    `function(){document.fonts.check(SPEC)?mark(2):fb()},fb)}else{fb()}}catch(e){fb()}}` +
    `function up(l){l.media='all';glyphs()}` +
    // Don't fall back on a font that is merely slow: defer while anything is still loading, up to D.
    `function net(){if(document.fonts&&document.fonts.status==='loading'&&Date.now()<D){` +
    `setTimeout(net,250);return}fb()}` +
    `function run(){D=Date.now()+${SYMBOLS_REVEAL_DEADLINE_MS};` +
    `var n=document.querySelectorAll('link[data-aqx-symbols]');` +
    `for(var i=0;i<n.length;i++){(function(l){` +
    `if(l.sheet){up(l)}else{l.addEventListener('load',function(){up(l)},{once:true});` +
    `l.addEventListener('error',fb,{once:true})}` +
    `})(n[i])}setTimeout(net,${SYMBOLS_REVEAL_TIMEOUT_MS})}` +
    `if(document.readyState==='loading'){` +
    `document.addEventListener('DOMContentLoaded',run,{once:true})}else{run()}})();`;
/**
 * The React-DOM twin of {@link PROMOTE_JS}. A `<script>` emitted by a CLIENT React render NEVER executes
 * (React DOM creates script nodes inertly), so in the builder canvas — a plain client tree, no SSR HTML,
 * no iframe — the sheet stayed at `media="print"` forever: the `@font-face` never registered, and every
 * glyph painted its LIGATURE TEXT in the fallback serif ("local_shipping", "check_circle"…). The inline
 * script still covers SSR/no-JS (storefront, noscript); this effect covers the client-only hosts.
 * Idempotent and unguarded by `__aqxSymbolsFont` on purpose: whichever path runs first, the other is a
 * no-op on links already promoted.
 */
function usePromoteSymbolsSheet() {
    useEffect(() => {
        // 0 clamped · 1 fallback (visible, still boxed) · 2 font (clamp dropped). Never walks back.
        let state = 0;
        const mark = (v) => {
            if (v <= state)
                return;
            state = v;
            document.documentElement.setAttribute('data-aqx-symbols-ready', v === 2 ? 'font' : 'fallback');
        };
        const fallback = () => mark(1);
        // Wait for the glyphs themselves, not just the sheet — see PROMOTE_JS.
        const waitForGlyphs = () => {
            const fonts = document.fonts;
            if (!fonts?.load)
                return fallback();
            fonts
                .load(SYMBOLS_FONT_SPEC)
                .then(() => (fonts.check(SYMBOLS_FONT_SPEC) ? mark(2) : fallback()), fallback);
        };
        const links = document.querySelectorAll('link[data-aqx-symbols]');
        const cleanups = [];
        links.forEach((l) => {
            if (l.media === 'all') {
                waitForGlyphs();
                return;
            }
            if (l.sheet) {
                l.media = 'all';
                waitForGlyphs();
                return;
            }
            const up = () => {
                l.media = 'all';
                waitForGlyphs();
            };
            l.addEventListener('load', up, { once: true });
            l.addEventListener('error', fallback, { once: true });
            cleanups.push(() => {
                l.removeEventListener('load', up);
                l.removeEventListener('error', fallback);
            });
        });
        // Don't fall back on a font that is merely slow: defer while anything is still loading, up to the cap.
        const deadline = Date.now() + SYMBOLS_REVEAL_DEADLINE_MS;
        let timer = 0;
        const net = () => {
            const fonts = document.fonts;
            if (fonts?.status === 'loading' && Date.now() < deadline) {
                timer = setTimeout(net, 250);
                return;
            }
            fallback();
        };
        timer = setTimeout(net, SYMBOLS_REVEAL_TIMEOUT_MS);
        return () => {
            clearTimeout(timer);
            cleanups.forEach((fn) => fn());
        };
    }, []);
}
/**
 * Loads the Material Symbols webfont: the stylesheet `<link>` + the global `.material-symbols-outlined`
 * glyph class. Render once inside any section/block that draws {@link MaterialIcon} (the browser dedupes
 * the link by URL, so multiple instances are harmless). The `<link>` href is the page's tiny `icon_names`
 * SUBSET when the render context carries one (storefront), else the full webfont (builder/standalone).
 * SSR-safe (plain markup, no client runtime).
 */
export function MaterialSymbolsFont() {
    const ctx = useRenderContextOptional();
    const href = ctx?.materialSymbolsHref ?? MATERIAL_SYMBOLS_HREF;
    usePromoteSymbolsSheet();
    return (_jsxs(_Fragment, { children: [_jsx("link", { rel: "stylesheet", href: href, media: "print", "data-aqx-symbols": "" }), _jsx("style", { dangerouslySetInnerHTML: { __html: FONT_CSS } }), _jsxs("noscript", { children: [_jsx("link", { rel: "stylesheet", href: href }), _jsx("style", { dangerouslySetInnerHTML: { __html: NOSCRIPT_CSS } })] }), _jsx("script", { dangerouslySetInnerHTML: { __html: PROMOTE_JS } })] }));
}
/**
 * Renders one icon: a custom image if provided, else the Material Symbols glyph, else (for a non-symbol
 * value such as a pasted emoji) the literal text. Size resolves to `var(--aqx-icon-size, <size>px)` so a
 * host can drive it responsively. Requires {@link MaterialSymbolsFont} somewhere in the same document.
 */
export function MaterialIcon({ name, size, filled, color, image, boldness }) {
    const dim = `var(--aqx-icon-size, ${size}px)`;
    if (image?.url) {
        return (_jsx("img", { src: image.url, alt: image.alt ?? '', style: { inlineSize: dim, blockSize: dim, objectFit: 'contain', display: 'block' } }));
    }
    const symbol = resolveSymbolName(name);
    const base = { lineHeight: 1, color };
    if (SYMBOL_TOKEN.test(symbol)) {
        const glyphStyle = {
            ...base,
            fontSize: dim,
            fontVariationSettings: `'FILL' ${filled ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' 24`,
        };
        if (boldness && boldness > 0) {
            // Thicken via an equal text-stroke in the glyph's own colour (the wght axis is pinned — see props).
            // em units so the stroke tracks the glyph's font-size (var(--aqx-icon-size)) across breakpoints.
            glyphStyle.WebkitTextStrokeWidth = `${boldness}em`;
            glyphStyle.WebkitTextStrokeColor = color ?? 'currentColor';
        }
        return (_jsx("span", { className: "material-symbols-outlined", "aria-hidden": "true", style: glyphStyle, children: symbol }));
    }
    // Non-symbol value: a pasted EMOJI (no ascii letters) renders as-is; a mistyped/invalid NAME (has ascii
    // letters or spaces, e.g. "Percent Discount") is NOT a real Material Symbols glyph, so render NOTHING
    // rather than the broken literal text the webfont would otherwise show.
    if (/[a-z]/i.test(symbol))
        return null;
    return (_jsx("span", { "aria-hidden": "true", style: {
            ...base,
            fontSize: `var(--aqx-icon-size, ${Math.round(size * 0.85)}px)`,
            display: 'inline-block',
        }, children: symbol }));
}
//# sourceMappingURL=materialSymbols.js.map